/** State helpers for the builder: undo/redo history, auto-save with revision checks, and page queries. */

import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../api.ts';
import { DEFAULT_SETTINGS } from './BuilderBar.tsx';
import { useRollup } from './rollup-context.ts';
import {
  reportsApi,
  type CompareSetting,
  type DocumentSettings,
  type Page,
  type RangeSetting,
  type ReportDocument,
  type Widget,
  type WidgetResult,
} from './reports-api.ts';

const HISTORY_LIMIT = 100;

/** Undo/redo over the page list. `set` records a step; `replace` changes state without one (e.g. adopting saved ids). */
export function useHistory(initial: Page[]) {
  const [state, setState] = useState({
    past: [] as Page[][],
    present: initial,
    future: [] as Page[][],
  });
  const set = useCallback((next: Page[]) => {
    setState((current) => ({
      past: [...current.past, current.present].slice(-HISTORY_LIMIT),
      present: next,
      future: [],
    }));
  }, []);
  const replace = useCallback(
    (next: Page[]) => setState((current) => ({ ...current, present: next })),
    [],
  );
  const undo = useCallback(() => {
    setState((current) => {
      const previous = current.past[current.past.length - 1];
      return previous
        ? {
            past: current.past.slice(0, -1),
            present: previous,
            future: [current.present, ...current.future],
          }
        : current;
    });
  }, []);
  const redo = useCallback(() => {
    setState((current) => {
      const [next, ...rest] = current.future;
      return next
        ? { past: [...current.past, current.present], present: next, future: rest }
        : current;
    });
  }, []);
  return {
    pages: state.present,
    set,
    replace,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
  };
}

export type SaveState = 'saved' | 'unsaved' | 'saving' | 'conflict' | 'error';

/**
 * Saves the whole layout a moment after the last edit. The revision travels
 * with every save; a 409 means someone else saved first, and the builder stops
 * saving until the user reloads — their edits stay on screen, nothing is lost.
 */
export function useAutosave(input: {
  organisationId: string;
  document: ReportDocument;
  name: string;
  settings: DocumentSettings;
  theme: Record<string, unknown>;
  pages: Page[];
  onSaved: (document: ReportDocument, pages: Page[], sentPages: Page[]) => void;
}) {
  const [state, setState] = useState<SaveState>('saved');
  const revision = useRef(input.document.revision);
  const blocked = useRef(false);
  const latest = useRef(input);
  latest.current = input;
  // What the server last stored. An effect run whose content equals it is not
  // an edit — e.g. adopting the ids a save returned — and must not save again.
  const fingerprint = (
    name: string,
    settings: DocumentSettings,
    theme: Record<string, unknown>,
    pages: Page[],
  ) => JSON.stringify([name, settings, theme, pages]);
  const lastSaved = useRef(
    fingerprint(input.document.name, input.settings, input.theme, input.pages),
  );

  useEffect(() => {
    revision.current = input.document.revision;
  }, [input.document.revision]);

  useEffect(() => {
    if (blocked.current) return undefined;
    if (fingerprint(input.name, input.settings, input.theme, input.pages) === lastSaved.current) {
      setState('saved');
      return undefined;
    }
    setState('unsaved');
    const timer = window.setTimeout(async () => {
      const { organisationId, document, name, settings, theme, pages, onSaved } = latest.current;
      setState('saving');
      try {
        const saved = await reportsApi.save(organisationId, document.id, {
          revision: revision.current,
          name,
          settings,
          theme,
          pages,
        });
        revision.current = saved.document.revision;
        lastSaved.current = fingerprint(saved.document.name, settings, theme, saved.pages);
        onSaved(saved.document, saved.pages, pages);
        setState('saved');
      } catch (error) {
        if (error instanceof ApiError && error.code === 'RESOURCE_CONFLICT') {
          blocked.current = true;
          setState('conflict');
        } else {
          setState('error');
        }
      }
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [input.name, input.settings, input.theme, input.pages]);

  return state;
}

export function widgetQuery(widget: Widget, key: string) {
  if (!widget.data.source || !(widget.data.metrics ?? []).length) return null;
  return {
    id: key,
    source: widget.data.source,
    metrics: widget.data.metrics,
    dimension: widget.data.dimension ?? null,
    filters: (widget.data.filters ?? []).filter((filter) => filter.values.length > 0),
    sort: widget.data.sort,
    limit: widget.data.limit,
    ...(widget.data.range ? { range: widget.data.range } : {}),
    ...(widget.data.client_id ? { client_id: widget.data.client_id } : {}),
  };
}

/** The document's period and mode, with defaults filled in. */
export function queryOf(settings: DocumentSettings) {
  return {
    mode: settings.mode ?? DEFAULT_SETTINGS.mode,
    timezone: settings.timezone ?? DEFAULT_SETTINGS.timezone,
    range: settings.range ?? DEFAULT_SETTINGS.range,
    compare: settings.compare ?? DEFAULT_SETTINGS.compare,
  };
}

/** Runs the page's queries; re-runs only when what is asked changes, not when widgets merely move. */
export function useQueries(input: {
  organisationId: string;
  businessUnitId: string | null;
  /** Roll-ups; taken from the builder's roll-up context when not given. */
  clientIds?: string[] | undefined;
  mode: 'LIVE' | 'SAMPLE';
  timezone: string;
  range: RangeSetting;
  compare: CompareSetting;
  widgets: Widget[];
  keyOf: (index: number) => string;
}) {
  const [results, setResults] = useState<Record<string, WidgetResult>>({});
  const [error, setError] = useState<string | null>(null);
  const rollup = useRollup();
  const clientIds = input.clientIds ?? rollup?.clientIds;
  const queries = input.widgets
    .map((widget, index) => widgetQuery(widget, input.keyOf(index)))
    .filter((query) => query !== null);
  const signature = JSON.stringify([
    input.mode,
    input.timezone,
    input.range,
    input.compare,
    input.businessUnitId,
    clientIds,
    queries,
  ]);
  useEffect(() => {
    if (queries.length === 0) {
      setResults({});
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      reportsApi
        .query(input.organisationId, {
          business_unit_id: input.businessUnitId,
          ...(clientIds ? { client_ids: clientIds } : {}),
          mode: input.mode,
          timezone: input.timezone,
          range: input.range,
          compare: input.compare,
          widgets: queries,
        })
        .then((data) => {
          if (cancelled) return;
          setResults(Object.fromEntries(data.results.map((result) => [result.id, result])));
          setError(null);
        })
        .catch(
          (problem: unknown) =>
            !cancelled &&
            setError(problem instanceof Error ? problem.message : 'The data could not be loaded.'),
        );
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // The signature captures every input that changes the answer, so it is the only dependency.
  }, [signature]);
  return { results, error };
}

/** The document's period as real dates ("1 – 30 Sep 2026"), resolved by the server even on a page with no widgets. */
export function usePeriod(input: {
  organisationId: string;
  businessUnitId: string | null;
  mode: 'LIVE' | 'SAMPLE';
  timezone: string;
  range: RangeSetting;
}) {
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);
  const signature = JSON.stringify([input.mode, input.timezone, input.range, input.businessUnitId]);
  useEffect(() => {
    let cancelled = false;
    reportsApi
      .query(input.organisationId, {
        business_unit_id: input.businessUnitId,
        mode: input.mode,
        timezone: input.timezone,
        range: input.range,
        widgets: [],
      })
      .then((data) => !cancelled && setPeriod(data.range))
      .catch(() => !cancelled && setPeriod(null));
    return () => {
      cancelled = true;
    };
    // The signature captures every input that changes the answer.
  }, [signature]);
  return period;
}
